import { dateTimeNowForExcel } from "../../../utils/format";
import { INNER_BORDER_COLOR, OUTER_BORDER_COLOR } from "../../styles/colors";
import {
  TOTAL_NUMBER_FORMAT,
  allEdges,
  applyGridStyle,
  applyRangeFormats,
  rangeFormatting,
} from "../../styles/worksheet";
import { safeWorksheetName } from "../../workbook";
import { generateAlternatesSOVReport } from "../alternatesSOV";
import { columnNames, rangeNames, rowNames } from "../helpers/rangeNames";
import { titleMaker } from "./common";

export const createAlternatesSOVTemplate = async () => {
  return Excel.run(async (context) => {
    const workbook = context.workbook;
    const safeName = await safeWorksheetName(workbook, "VM Decisions SOV");
    const sheet = workbook.worksheets.add(safeName);
    await context.sync();

    await titleMaker(sheet, "Schedule of Values");

    const rowHeights = [12, 30, 25, 25, 12, 25];
    rowHeights.forEach((height, index) => {
      sheet.getRange(`${index + 5}:${index + 5}`).format.rowHeight = height;
    });

    const columnWidths = [20, 50, 400, 130, 130, 12];
    columnWidths.forEach((width, index) => {
      sheet.getRange(`${String.fromCharCode(65 + index)}:${String.fromCharCode(65 + index)}`).format.columnWidth =
        width;
    });

    sheet.getRange("1:15").format.verticalAlignment = Excel.VerticalAlignment.center;
    await context.sync();

    // Create the table header
    const headers: rangeFormatting[] = [
      { rangeAddress: "B6", value: "Code", name: "\\code", Halignment: "Center" },
      { rangeAddress: "C6", value: "Description", indentLevel: 1, name: "\\name", Halignment: "Left" },
      { rangeAddress: "D6:E6", name: `\\${columnNames.alternates}`, Halignment: "Center" },
    ];
    headers.forEach((header) => {
      header.fontSize = 12;
      header.bold = true;
    });
    applyRangeFormats(sheet, headers);

    // Create the table rows
    const dataRows: rangeFormatting[] = [
      {
        // the items named range cells must first be two cells wide for the borders to create correctly
        rangeAddress: "B7:B8",
        name: rowNames.alternatesSOV,
        Halignment: "Center",
        numberFormat: "@",
      },
      { rangeAddress: "C7", Halignment: "Left", indentLevel: 1 },
      {
        rangeAddress: "D1",
        value: dateTimeNowForExcel(),
        numberFormat: "mm/dd/yyyy",
        bold: true,
        name: rangeNames.date,
      },
      { rangeAddress: "D7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
      { rangeAddress: "E7", numberFormat: TOTAL_NUMBER_FORMAT, Halignment: "Right" },
      { rangeAddress: "C10", value: "Total", indentLevel: 1, bold: true, Halignment: "Right" },
      {
        rangeAddress: "D10",
        numberFormat: TOTAL_NUMBER_FORMAT,
        Halignment: "Right",
        value: "=sum(D6:D9)",
        bold: true,
      },
      {
        rangeAddress: "E10",
        numberFormat: TOTAL_NUMBER_FORMAT,
        Halignment: "Right",
        value: "=sum(E6:E9)",
        bold: true,
      },
    ];
    applyRangeFormats(sheet, dataRows);

    // sov items, must be two rows for the borders to create correctly
    applyGridStyle({
      range: sheet.getRange("B7:E8"),
      borderColor: INNER_BORDER_COLOR,
      weight: "Thin",
      edges: allEdges,
    });
    applyGridStyle({
      range: sheet.getRange("A7"),
      borderColor: OUTER_BORDER_COLOR,
      weight: "Medium",
      edges: ["EdgeRight"],
    });
    applyGridStyle({
      range: sheet.getRange("F7"),
      borderColor: OUTER_BORDER_COLOR,
      weight: "Medium",
      edges: ["EdgeLeft"],
    });
    // spacer row
    applyGridStyle({
      range: sheet.getRange("B9:E9"),
      borderColor: OUTER_BORDER_COLOR,
      weight: "Medium",
      edges: ["EdgeTop"],
    });
    // subtotal
    applyGridStyle({
      range: sheet.getRange("C10"),
      borderColor: OUTER_BORDER_COLOR,
      weight: "Medium",
      edges: ["EdgeRight"],
    });
    applyGridStyle({
      range: sheet.getRange("F10"),
      borderColor: OUTER_BORDER_COLOR,
      weight: "Medium",
      edges: ["EdgeLeft"],
    });
    applyGridStyle({
      range: sheet.getRange("D9:E9"),
      borderColor: OUTER_BORDER_COLOR,
      weight: "Medium",
      edges: ["EdgeBottom"],
    });
    applyGridStyle({
      range: sheet.getRange("D11:E11"),
      borderColor: OUTER_BORDER_COLOR,
      weight: "Medium",
      edges: ["EdgeTop"],
    });

    sheet.showGridlines = false;
    await context.sync();

    await generateAlternatesSOVReport(sheet);
    sheet.activate();
    await context.sync();
    // and then the header
    applyGridStyle({
      range: sheet.getRange("B6:C6"),
      borderColor: OUTER_BORDER_COLOR,
      weight: "Medium",
      edges: allEdges,
    });
    const alternatesHeaderRange = sheet.getRange(`\\${columnNames.alternates}`);
    applyGridStyle({
      range: alternatesHeaderRange,
      borderColor: OUTER_BORDER_COLOR,
      weight: "Medium",
      edges: allEdges,
    });

    sheet.showGridlines = false;
    sheet.activate();
    await context.sync();
  });
};
